import React, { useMemo, useState, useRef, useEffect } from "react";

type Op = "+" | "-" | "×" | "÷" | "mixed";

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOp(op: Op) {
  if (op !== "mixed") return op;
  const ops: Op[] = ["+", "-", "×", "÷"];
  return ops[randInt(0, ops.length - 1)];
}

function generateQuestion(opIn: Op, maxOperand: number) {
  const op = pickOp(opIn);
  let a = randInt(1, maxOperand);
  let b = randInt(1, maxOperand);

  if (op === "-") {
    if (a < b) [a, b] = [b, a];
    return { text: `${a} - ${b}`, answer: String(a - b) };
  }

  if (op === "÷") {
    // ensure integer division: a = b * c
    b = randInt(1, Math.max(1, Math.floor(maxOperand / 2)));
    const c = randInt(1, maxOperand);
    a = b * c;
    return { text: `${a} ÷ ${b}`, answer: String(c) };
  }

  if (op === "×") {
    return { text: `${a} × ${b}`, answer: String(a * b) };
  }

  // addition
  return { text: `${a} + ${b}`, answer: String(a + b) };
}

export function MathApp() {
  // single-loop game state
  const [op, setOp] = useState<Op>("+");
  const [max, setMax] = useState<number>(10);
  const [question, setQuestion] = useState(() => generateQuestion(op, max));
  const [userAnswer, setUserAnswer] = useState<string>("");
  const [feedback, setFeedback] = useState<{
    correct: boolean;
    closeness?: number;
    message: string;
  } | null>(null);
  const [streak, setStreak] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const opsOptions = useMemo(() => ["+", "-", "×", "÷", "mixed"], []);

  function nextQuestion() {
    setQuestion(generateQuestion(op, max));
    setUserAnswer("");
    setFeedback(null);
    // focus input after new problem
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }

  function submitAnswer() {
    // only block submission if the feedback is a successful one (we're auto-advancing)
    if (feedback?.correct) return;
    const current = question;
    const cleanUser = userAnswer.trim();
    if (cleanUser === "") return;

    const userNum = Number(cleanUser);
    const correctStr = String(current.answer).trim();
    const correctNum = Number(correctStr);

    if (!Number.isNaN(userNum) && !Number.isNaN(correctNum)) {
      const diff = Math.abs(userNum - correctNum);
      const closeness = Math.max(0, 100 - Math.round((diff / Math.max(1, Math.abs(correctNum))) * 100));
      if (userNum === correctNum) {
        setFeedback({ correct: true, closeness: 100, message: `✅ Correct — ${closeness}%` });
        setStreak(s => s + 1);
        setTimeout(() => {
          nextQuestion();
        }, 700);
      } else {
        setFeedback({ correct: false, closeness, message: `❌ ${closeness}% close — try again` });
        setStreak(0); // reset streak immediately on wrong answer
        // focus input for retry
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    } else {
      const correct = correctStr === cleanUser;
      if (correct) {
        setFeedback({ correct: true, message: `✅ Correct` });
        setStreak(s => s + 1);
        setTimeout(() => nextQuestion(), 700);
      } else {
        setFeedback({ correct: false, message: `❌ Wrong — try again` });
        setStreak(0); // reset streak immediately on wrong answer
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }
    setUserAnswer("");
  }

  return (
  <div className="mx-auto w-full max-w-3xl text-center">
  <div className="flex flex-col md:flex-row gap-4 mb-4 items-center justify-center text-center">
        <div className="flex flex-col items-center gap-2">
          <label className="font-medium">Operation</label>
          <div className="flex gap-2">
            {opsOptions.map(o => (
              <button
                key={o}
                className={`px-3 py-1 rounded border ${op === o ? 'bg-blue-600 text-white' : 'bg-white'}`}
                onClick={() => {
                  setOp(o as Op);
                  setQuestion(generateQuestion(o as Op, max));
                  setFeedback(null);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }}
              >
                {o}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          <label className="font-medium mb-1">Scale</label>
          <div className="flex gap-2">
            {[10, 20, 50, 100].map(val => (
              <label key={val} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="max"
                  value={val}
                  checked={max === val}
                  onChange={() => {
                    setMax(val);
                    setQuestion(generateQuestion(op, val));
                    setFeedback(null);
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  }}
                  className="accent-blue-600"
                />
                <span className={`px-2 py-1 rounded ${max === val ? 'bg-blue-600 text-white' : 'bg-white'}`}>{val}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="flex gap-2">
            <button className="px-3 py-1 rounded border" onClick={() => nextQuestion()}>
              New Problem
            </button>
            <button className="px-3 py-1 rounded border" onClick={() => { setStreak(0); setFeedback(null); }}>
              Reset Streak
            </button>
          </div>
        </div>
      </div>

      <div className="bg-card p-6 rounded-xl border border-input">
        <div className="flex flex-col items-center gap-4">
          <div className="text-4xl font-bold">{question.text}</div>

          <input
            ref={inputRef}
            value={userAnswer}
            onChange={e => setUserAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submitAnswer(); } }}
            className="px-3 py-2 rounded border text-lg text-center w-48"
            inputMode="numeric"
            disabled={false}
          />

          <div className="flex gap-2">
            <button className="px-4 py-2 rounded bg-green-600 text-white" onClick={submitAnswer}>
              Submit
            </button>
            <button className="px-4 py-2 rounded border" onClick={() => { setUserAnswer(''); setFeedback(null); }}>
              Clear
            </button>
          </div>

          <div className="text-sm text-muted-foreground">Streak: {streak}</div>

          <div className="mt-2 w-full" style={{ minHeight: '3rem' }}>
            {feedback ? (
              <div className={`p-2 rounded border text-center w-full ${feedback.correct ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                {feedback.message}
              </div>
            ) : (
              <div className="w-full" style={{ visibility: 'hidden' }}>&nbsp;</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MathApp;
